/**
 * Werkora API Client
 * Centralized API configuration for deployment-agnostic operation
 * Works on: Emergent, Render, any Node hosting
 * 
 * Environment Variables:
 * - REACT_APP_BACKEND_URL: Base URL for API calls
 *   - Local: http://localhost:8001 or empty (relative)
 *   - Render: /api (relative) or full URL
 *   - Emergent: Uses REACT_APP_BACKEND_URL from .env
 */

// Get API base URL from environment
const getApiBase = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  if (envUrl) {
    // Remove trailing slashes and return
    return envUrl.replace(/\/+$/, "");
  }
  
  // Fallback to empty string for relative paths
  return "";
};

const API_BASE = getApiBase();

/**
 * Build full API URL from path
 * Automatically adds /api prefix if not present
 */
export const apiUrl = (path = "") => {
  let normalizedPath = path;
  
  // Ensure path starts with /api
  if (!normalizedPath.startsWith("/api") && normalizedPath.startsWith("/")) {
    normalizedPath = `/api${normalizedPath}`;
  } else if (!normalizedPath.startsWith("/")) {
    normalizedPath = `/api/${normalizedPath}`;
  }
  
  return `${API_BASE}${normalizedPath}`;
};

/**
 * Default fetch options for all API requests
 */
const getDefaultOptions = () => ({
  credentials: "include", // Required for cookies/sessions
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Make API request with automatic error handling
 */
export const apiFetch = async (path, options = {}) => {
  const url = apiUrl(path);
  const defaultOpts = getDefaultOptions();
  
  const finalOptions = {
    ...defaultOpts,
    ...options,
    headers: {
      ...defaultOpts.headers,
      ...options.headers,
    },
  };
  
  try {
    const response = await fetch(url, finalOptions);
    
    // Handle non-JSON responses
    const contentType = response.headers.get("content-type");
    
    if (!response.ok) {
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || `API Error: ${response.status}`);
      }
      const text = await response.text();
      throw new Error(text || `API Error: ${response.status}`);
    }
    
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    }
    
    return response.text();
  } catch (error) {
    // Log error for debugging but don't expose internals
    console.error("API Error:", error.message);
    throw error;
  }
};

/**
 * API methods for common operations
 */
export const api = {
  get: (path) => apiFetch(path, { method: "GET" }),
  
  post: (path, data) => apiFetch(path, {
    method: "POST",
    body: data ? JSON.stringify(data) : undefined,
  }),
  
  put: (path, data) => apiFetch(path, {
    method: "PUT",
    body: data ? JSON.stringify(data) : undefined,
  }),
  
  delete: (path) => apiFetch(path, { method: "DELETE" }),
  
  // Special method for form data (file uploads)
  upload: async (path, formData) => {
    const url = apiUrl(path);
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary
    });
    
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Upload Error: ${response.status}`);
    }
    
    return response.json();
  },
};

/**
 * Check if user is authenticated
 * Returns user data if authenticated, null otherwise
 */
export const checkAuth = async () => {
  try {
    const response = await apiFetch("/auth/me");
    return response.user || response;
  } catch (error) {
    // Auth check failed - user not logged in
    return null;
  }
};

export default API_BASE;
